import { useState } from "react";
import { CorrectionsList } from "../components/brand/CorrectionsList";
import { useArchiveCorrection, useBrandCorrections } from "../hooks/useBrandCorrections";

interface BrandCorrectionsProps {
  brandId: string;
}

// Verified path: assembleBrandContext() reads getEditHistory(brandId, 10), so
// the most recent 10 active corrections are part of brand context on future runs.
const BANNER_COPY =
  "The agent includes your most recent 10 active corrections in future runs. Archive corrections that no longer apply.";

export function BrandCorrections({ brandId }: BrandCorrectionsProps) {
  const [showArchived, setShowArchived] = useState(false);
  const archivedSectionId = "archived-corrections";
  const { data: activeData, isLoading: isActiveLoading } = useBrandCorrections(brandId, { active: true });
  const { data: archivedData, isLoading: isArchivedLoading } = useBrandCorrections(brandId, {
    active: false,
    enabled: showArchived,
  });
  const archiveCorrection = useArchiveCorrection(brandId);

  const activeEdits = (activeData?.data ?? []).slice(0, 10);
  const archivedEdits = archivedData?.data ?? [];

  if (isActiveLoading || (showArchived && isArchivedLoading)) {
    return <div className="text-sm text-slate-500">Loading corrections…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">Recent corrections</h3>
        <p className="mt-1 text-sm text-slate-600">{BANNER_COPY}</p>
      </div>

      <CorrectionsList
        edits={activeEdits}
        onArchive={(correctionId) => void archiveCorrection.mutateAsync(correctionId)}
        archivingId={archiveCorrection.isPending ? archiveCorrection.variables ?? null : null}
      />

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setShowArchived((current) => !current)}
          aria-expanded={showArchived}
          aria-controls={archivedSectionId}
          className="text-sm font-medium text-brand-700 hover:text-brand-800 hover:underline"
        >
          {showArchived ? "Hide archived" : "Show archived"}
        </button>
      </div>

      {showArchived && (
        <div id={archivedSectionId}>
          <CorrectionsList edits={archivedEdits} readOnly />
        </div>
      )}
    </div>
  );
}

export { BANNER_COPY as BRAND_CORRECTIONS_BANNER_COPY };
